// Project Manager → Projects (/pm).
//
// The PM decides what gets tested and by whom. This page is where a project is
// born and staffed; everything downstream — which projects a tester can open,
// who gets the report email, who may mark a fix — follows from the assignments
// made here.
//
// A project with no tester assigned can never be RUN, which is a silent dead end
// if you only find out by waiting for results that never come. That's flagged on
// the row, not just validated in the form.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus, FolderKanban, Loader2, Users, AlertTriangle, Trash2, X, Check,
  UserPlus, Search,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import DataTable from "@/components/dashboard/DataTable";
import Modal from "@/components/dashboard/Modal";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { useSurfaceBase } from "@/hooks/useSurfaceBase";
import { useApiData } from "@/hooks/useApiData";
import {
  getProjects, createProject, deleteProject,
  getProjectAssignees, assignToProject, unassignFromProject,
} from "@/api/projects";
import { cn } from "@/lib/cn";
import { STATUS_ORDER, statusMeta } from "@/lib/projectStatus";
import { formatDate } from "@/lib/dates";

const URL_RE = /^https?:\/\/.+/i;

function relTime(v) {
  if (!v) return "Never";
  const ms = Date.now() - new Date(v).getTime();
  if (Number.isNaN(ms)) return "Never";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d < 30 ? `${d}d ago` : `${Math.floor(d / 30)}mo ago`;
}

const fmtDate = (v) => formatDate(v);

function initials(name, email) {
  const s = name || email || "?";
  return s.split(/[\s@.]+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

// ---------------------------------------------------------------------------
// One person, pickable. Used for both the assigned and the available columns.
// ---------------------------------------------------------------------------
function PersonRow({ person, selected, onToggle, action }) {
  const pending = person.invite_status === "pending";
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors",
        selected
          ? "border-brand-400 bg-brand-50/70 dark:border-brand-500/40 dark:bg-brand-500/10"
          : "border-slate-200 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5",
      )}
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600 dark:bg-white/10 dark:text-slate-200">
        {initials(person.name, person.email)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-100">
          {person.name || person.email}
        </span>
        <span className="block truncate text-xs text-slate-400">{person.email}</span>
      </span>
      {pending && (
        <Badge tone="amber" className="shrink-0">Invite pending</Badge>
      )}
      {action}
      {selected && <Check size={16} className="shrink-0 text-brand-600" />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Staffing panel — assign testers and developers in ONE action.
// Assigning five people to twenty projects one at a time is unusable.
// ---------------------------------------------------------------------------
function StaffPanel({ project, onClose, onChanged }) {
  const [roster, setRoster] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [confirmUnassign, setConfirmUnassign] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRoster(await getProjectAssignees(project.id));
    } catch (e) {
      // `false` (not null) so the body can tell "still loading" from "failed" —
      // left as null it showed an error banner above a spinner that never stopped
      // and offered no way back.
      setRoster(false);
      setError((e && e.message) || "Could not load the team.");
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  function toggle(id) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function assign() {
    if (!picked.size) return;
    setBusy(true); setError(null);
    try {
      await assignToProject(project.id, [...picked]);
      setPicked(new Set());
      await load();
      onChanged?.();
    } catch (e) {
      setError((e && e.message) || "Could not assign.");
    } finally {
      setBusy(false);
    }
  }

  // Confirmed first: the server deletes the assignment, writes an audit row and
  // notifies the person. A stray click on a 32px × used to do all three.
  // Through the app's own dialog, not window.confirm — a native prompt ignores
  // the theme, can be suppressed by the browser, and looks like a phishing box.
  async function unassign() {
    const person = confirmUnassign;
    if (!person) return;
    setBusy(true); setError(null);
    try {
      await unassignFromProject(project.id, person.id);
      setConfirmUnassign(null);
      await load();
      onChanged?.();
    } catch (e) {
      setError((e && e.message) || "Could not remove.");
    } finally {
      setBusy(false);
    }
  }

  const match = (p) => {
    const s = q.trim().toLowerCase();
    return !s || `${p.name || ""} ${p.email}`.toLowerCase().includes(s);
  };

  // "Everyone is already on it" was keyed off the FILTERED list, so a search that
  // matched nobody claimed the whole roster was already assigned.
  const emptyPeopleLabel = (unfiltered) =>
    q.trim() && (unfiltered || []).length
      ? `Nobody matches “${q.trim()}”.`
      : "Everyone is already on it.";

  const assigned = [
    ...(roster?.assigned_testers || []).map((p) => ({ ...p, kind: "tester" })),
    ...(roster?.assigned_developers || []).map((p) => ({ ...p, kind: "developer" })),
  ];
  const availableTesters = (roster?.available_testers || []).filter(match);
  const availableDevs = (roster?.available_developers || []).filter(match);

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={`Who works on ${project.name}?`}
      footer={
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-slate-400">
            Testers run the suite · developers fix what it finds.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Done</Button>
            <Button size="sm" onClick={assign} disabled={busy || !picked.size}>
              {busy ? <Loader2 size={15} className="animate-spin" />
                    : <><UserPlus size={15} /> Assign {picked.size || ""}</>}
            </Button>
          </div>
        </div>
      }
    >
      {error && (
        <div className="mb-4 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
          {error}
        </div>
      )}

      {roster === false ? (
        <div className="py-8 text-center">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            The team for this project couldn&apos;t be loaded.
          </p>
          <Button variant="outline" size="sm" className="mt-3" onClick={load}>Try again</Button>
        </div>
      ) : !roster ? (
        <div className="flex justify-center py-10 text-slate-400">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : (
        <div className="space-y-6">
          {!roster.has_tester && (
            <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>
                No tester on this project yet — until you assign one, nobody can add
                the repository or run anything.
              </span>
            </div>
          )}

          <section>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
              On this project ({assigned.length})
            </h4>
            {assigned.length === 0 ? (
              <p className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-400 dark:border-white/10">
                Nobody yet. Pick people below.
              </p>
            ) : (
              <div className="space-y-2">
                {assigned.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5 dark:border-white/10">
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600 dark:bg-white/10 dark:text-slate-200">
                      {initials(p.name, p.email)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                        {p.name || p.email}
                      </span>
                      <span className="block truncate text-xs text-slate-400">{p.email}</span>
                    </span>
                    <Badge tone={p.kind === "tester" ? "brand" : "slate"} className="shrink-0 capitalize">
                      {p.kind}
                    </Badge>
                    <button
                      type="button"
                      onClick={() => setConfirmUnassign(p)}
                      disabled={busy}
                      title="Remove from this project"
                      aria-label={`Remove ${p.name || p.email} from this project`}
                      className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50 dark:hover:bg-rose-500/10"
                    >
                      <X size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className="relative">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search people…"
              className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-400 focus:bg-white focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white"
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <section>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Testers ({availableTesters.length})
              </h4>
              <div className="space-y-2">
                {availableTesters.length === 0 ? (
                  <p className="text-xs text-slate-400">{emptyPeopleLabel(roster?.available_testers)}</p>
                ) : availableTesters.map((p) => (
                  <PersonRow key={p.id} person={p} selected={picked.has(p.id)}
                             onToggle={() => toggle(p.id)} />
                ))}
              </div>
            </section>
            <section>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Developers ({availableDevs.length})
              </h4>
              <div className="space-y-2">
                {availableDevs.length === 0 ? (
                  <p className="text-xs text-slate-400">{emptyPeopleLabel(roster?.available_developers)}</p>
                ) : availableDevs.map((p) => (
                  <PersonRow key={p.id} person={p} selected={picked.has(p.id)}
                             onToggle={() => toggle(p.id)} />
                ))}
              </div>
            </section>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmUnassign}
        title={confirmUnassign ? `Remove ${confirmUnassign.name || confirmUnassign.email}?` : ""}
        body={`They lose access to ${project.name} immediately and are notified. You can assign them again at any time.`}
        confirmLabel="Remove from project"
        busy={busy}
        onConfirm={unassign}
        onCancel={() => setConfirmUnassign(null)}
      />
    </Modal>
  );
}

// ---------------------------------------------------------------------------
export default function PmProjects() {
  // These screens are shared with the company Admin surface, so links
  // must stay inside whichever root the reader came in through.
  const base = useSurfaceBase();
  const navigate = useNavigate();
  // ?status=Active — how the Overview's testing-status chips land here already
  // narrowed to the slice the reader clicked.
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get("status");
  const { data, loading, error: loadError, reload } = useApiData(getProjects, []);
  // Memoised: a fresh [] each render would restart the staffing fetch forever.
  const projects = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const [create, setCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", base_url: "" });
  const [formError, setFormError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [staffing, setStaffing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  // Its own state: writing a delete failure into `formError` put the message
  // inside the (closed) create modal, so a failed delete looked like nothing
  // happened at all.
  const [delError, setDelError] = useState(null);
  // Staffing counts arrive WITH the project list (two grouped queries on the
  // server). This used to be a getProjectAssignees() call per project fired from
  // an effect — so the page made N+1 requests and got slower with every project.

  function closeCreate() {
    setCreate(false);
    setForm({ name: "", description: "", base_url: "" });
    setFormError(null);
  }

  async function submit() {
    const name = form.name.trim();
    const url = form.base_url.trim();
    if (!name) return setFormError("Give the project a name.");
    if (name.length > 255) return setFormError("That name is too long (max 255 characters).");
    if (!url) return setFormError("The app's URL is required — it's where testing starts.");
    if (!URL_RE.test(url)) return setFormError("The URL must start with http:// or https://");

    setBusy(true); setFormError(null);
    try {
      const created = await createProject({
        name, base_url: url, description: form.description.trim() || null,
      });
      closeCreate();
      await reload();
      // Straight into staffing — a project nobody is on does nothing.
      setStaffing({ id: created.project_id, name });
    } catch (e) {
      setFormError((e && e.message) || "Could not create the project.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(project) {
    setBusy(true); setDelError(null);
    try {
      await deleteProject(project.id);
      setConfirmDel(null);
      await reload();
    } catch (e) {
      setDelError((e && e.message) || "Could not delete the project.");
    } finally {
      setBusy(false);
    }
  }

  const rows = useMemo(() => projects.map((p) => ({
    ...p,
    testers: p.testers ?? 0,
    developers: p.developers ?? 0,
    // Size of the CURRENT suite. GET /project already returns this (and the
    // `generation` roll-up below) from one grouped query, so both are free here.
    test_cases: p.test_cases ?? 0,
    // Only meaningful once the list has actually loaded — flagging "can't be
    // run" against an empty list would scare the user during every refresh.
    needs_tester: !loading && (p.testers ?? 0) === 0,
    created: fmtDate(p.created_at),
    // Sort on the timestamp, not the localised string — "8/3/2026" vs
    // "12/25/2025" compares lexically and orders by month.
    created_ts: p.created_at ? Date.parse(p.created_at) || 0 : 0,
    generated: fmtDate(p.generation?.created_at),
    generated_ts: p.generation?.created_at ? Date.parse(p.generation.created_at) || 0 : 0,
    runs_total: p.runs_total ?? 0,
    bugs: p.bugs ?? 0,
    // Sort key only. `pass_rate` is null until something settles, and null sorts
    // unpredictably next to numbers — -1 keeps "no verdict yet" below 0%, which
    // is where it belongs, without letting it read as 0% in the cell.
    pass_sort: p.pass_rate == null ? -1 : p.pass_rate,
    testing_status: p.testing_status || "not_started",
    status_label: statusMeta(p.testing_status).label,
    last_run_ts: p.last_run_at ? Date.parse(p.last_run_at) || 0 : 0,
  })), [projects, loading]);

  const columns = [
    {
      key: "name", header: "Project", sortable: true,
      render: (r) => (
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="truncate font-semibold text-slate-800 dark:text-slate-100">{r.name}</p>
            {/* Staffing is a fact about the TEAM, not about the testing, so it
                rides here as a badge instead of occupying the Status column —
                which now reports what the project is actually doing. */}
            {r.needs_tester && (
              <Badge tone="amber" className="shrink-0" title="No tester assigned — this project cannot be run">
                <AlertTriangle size={11} /> No tester
              </Badge>
            )}
          </div>
          <p className="truncate text-xs text-slate-400">{r.base_url}</p>
        </div>
      ),
    },
    {
      key: "test_cases", header: "Test cases", sortable: true, align: "right",
      render: (r) => (
        r.test_cases ? (
          <div>
            <p className="tabular-nums font-semibold text-slate-800 dark:text-slate-100">{r.test_cases.toLocaleString()}</p>
            {r.generation && (
              <p className="whitespace-nowrap text-xs text-slate-400">v{r.generation.version} · {r.generated}</p>
            )}
          </div>
        ) : <span className="text-xs text-slate-400">None yet</span>
      ),
    },
    {
      key: "runs_total", header: "Runs", sortable: true, align: "right",
      render: (r) => (
        r.runs_total ? (
          <div>
            <p className="tabular-nums font-semibold text-slate-800 dark:text-slate-100">{r.runs_total.toLocaleString()}</p>
            <p className="whitespace-nowrap text-xs text-slate-400">
              {/* null means nothing has settled yet — not 0%. */}
              {r.pass_rate == null ? "no verdict yet" : `${r.pass_rate}% pass`}
            </p>
          </div>
        ) : <span className="text-xs text-slate-400">—</span>
      ),
    },
    {
      key: "bugs", header: "Bugs", sortable: true, align: "right",
      render: (r) => (
        r.bugs
          ? <span className="tabular-nums font-semibold text-rose-600 dark:text-rose-400">{r.bugs.toLocaleString()}</span>
          : <span className="text-xs text-slate-400">0</span>
      ),
    },
    {
      key: "status_label", header: "Status", sortable: true,
      render: (r) => {
        const meta = statusMeta(r.testing_status);
        return (
          <Badge tone={meta.tone} className="whitespace-nowrap" title={r.status_message || undefined}>
            {meta.live && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />}
            {r.status_paused ? "Paused" : meta.label}
          </Badge>
        );
      },
    },
    {
      key: "testers", header: "Staffing",
      render: (r) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={r.testers ? "brand" : "rose"}>
            {r.testers} tester{r.testers === 1 ? "" : "s"}
          </Badge>
          <Badge tone="slate">
            {r.developers} dev{r.developers === 1 ? "" : "s"}
          </Badge>
        </div>
      ),
    },
    {
      key: "last_run_ts", header: "Last activity", sortable: true,
      render: (r) => (
        <span className="whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">{relTime(r.last_run_at)}</span>
      ),
    },
    {
      key: "actions", header: "", align: "right",
      render: (r) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <Button variant="outline" size="sm" onClick={() => setStaffing(r)}>
            <Users size={14} /> Staff
          </Button>
          <button
            type="button"
            onClick={() => setConfirmDel(r)}
            title="Delete project"
            className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10"
          >
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  const noTesterCount = rows.filter((r) => r.needs_tester).length;

  // Only the statuses actually present, in STATUS_ORDER. Offering all eight would
  // list six that match nothing on most companies.
  const statusFilter = useMemo(() => {
    const present = new Set(rows.map((r) => r.testing_status));
    const options = STATUS_ORDER.filter((k) => present.has(k)).map((k) => statusMeta(k).label);
    return options.length > 1 ? [{ key: "status_label", label: "statuses", options }] : [];
  }, [rows]);

  return (
    <>
      <PageHeader
        title="Projects"
        subtitle="Create a project, then decide who tests it and who fixes what it finds."
        actions={
          <Button size="sm" onClick={() => setCreate(true)}>
            <Plus size={16} /> New project
          </Button>
        }
      />

      {noTesterCount > 0 && (
        <div className="mb-5 flex items-start gap-2.5 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>
            {noTesterCount} project{noTesterCount === 1 ? " has" : "s have"} no tester
            assigned. Nobody can add a repository or start a run on
            {noTesterCount === 1 ? " it" : " them"} until you staff
            {noTesterCount === 1 ? " it" : " them"}.
          </span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16 text-slate-400">
          <Loader2 size={22} className="animate-spin" />
        </div>
      ) : loadError ? (
        /* Distinct from the empty state: a failed request used to render "No
           projects yet", which reads as "your company has none". */
        <div className="card p-12 text-center">
          <AlertTriangle size={26} className="mx-auto mb-3 text-amber-500" />
          <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-white">
            Couldn&apos;t load your projects
          </h3>
          <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500 dark:text-slate-400">{loadError}</p>
          <Button className="mt-6" variant="outline" onClick={reload}>Try again</Button>
        </div>
      ) : rows.length === 0 ? (
        <div className="card p-12 text-center">
          <span className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-brand-500/10 text-brand-600 dark:text-brand-300">
            <FolderKanban size={26} />
          </span>
          <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-white">
            No projects yet
          </h3>
          <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500 dark:text-slate-400">
            Create one, assign a tester and a developer, and the tester takes it from
            there — repository, logins, generation, runs.
          </p>
          <Button className="mt-6" onClick={() => setCreate(true)}>
            <Plus size={16} /> New project
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          searchKeys={["name", "base_url", "status_label"]}
          filters={statusFilter}
          initialFilters={statusParam ? { status_label: statusParam } : null}
          dateKey="created_at"
          dateLabel="Created"
          pageSize={15}
          maxHeight="34rem"
          onRowClick={(r) => navigate(`${base}/projects/${r.id}`)}
          emptyLabel="No projects match those filters."
        />
      )}

      {/* create ------------------------------------------------------------ */}
      <Modal
        open={create}
        onClose={closeCreate}
        title="New project"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={closeCreate}>Cancel</Button>
            <Button size="sm" onClick={submit} disabled={busy}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : "Create project"}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {formError && (
            <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
              {formError}
            </div>
          )}
          <Input
            label="Project name"
            placeholder="e.g. Cardizo Web"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Input
            label="App URL"
            placeholder="https://app.example.com"
            value={form.base_url}
            onChange={(e) => setForm({ ...form, base_url: e.target.value })}
          />
          <Input
            label="Description (optional)"
            placeholder="What is this app, and what matters most about it?"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <p className="rounded-xl bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-500 dark:bg-white/5 dark:text-slate-400">
            No repository or token here — the tester you assign adds those, along with
            the logins, once they have the project. You&apos;ll pick people next.
          </p>
        </div>
      </Modal>

      {staffing && (
        <StaffPanel
          project={staffing}
          onClose={() => setStaffing(null)}
          onChanged={reload}
        />
      )}

      {/* delete ------------------------------------------------------------ */}
      <Modal
        open={!!confirmDel}
        onClose={() => { setConfirmDel(null); setDelError(null); }}
        title="Delete this project?"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => { setConfirmDel(null); setDelError(null); }}>Cancel</Button>
            <Button variant="danger" size="sm" onClick={() => remove(confirmDel)} disabled={busy}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : "Delete project"}
            </Button>
          </div>
        }
      >
        {delError && (
          <div className="mb-4 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
            {delError}
          </div>
        )}
        <p className="text-sm text-slate-600 dark:text-slate-300">
          <span className="font-semibold">{confirmDel?.name}</span> and everything under
          it — test cases, runs and reports — go with it. This can&apos;t be undone.
        </p>
      </Modal>
    </>
  );
}
